import React from 'react'
import _ from 'lodash'

import {Colors, Row} from '../UI/index.js'
import AddressAttachment from '../addresses/AddressAttachment.js'
import {formSpacing, AddressSearch, Check, FormRow, FormStack, TimePicker, Input, Select, TextArea, RadioGroup, Option} from '../UI/forms/index.js'
import PostalCodeAutocomplete from '../UI/forms/PostalCodeAutocomplete.tsx'

import Countries from '../../utils/Countries.js'
import AddressActions from '../../actions/AddressActions.js'
import {normalize} from '@utils/normalize.js'
import Languages from '@utils/Languages.js'
import dbCodes from 'server/dbCodes.js'

class EditAddress extends React.PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            address: null,
            suggestions: [],
            validation: {}
        }
    }

    componentDidMount() {
        if (this.props.onSave) {
            this.setState({address: JSON.parse(JSON.stringify(this.props.address))})
        }
    }

    setName(event, changeOnExactMatch = true) {
        const {validation} = this.state

        if (validation.name) {
            validation.name = null
            this.setState({validation})
        }

        const {addressBook = []} = this.props
        const address = JSON.parse(JSON.stringify(this.props.address))

        const suggestions = event.target.value.length < 1 ?
            [] :
            addressBook.filter((value) => {
                return normalize(value.name.toLowerCase()).indexOf(normalize(event.target.value.toLowerCase())) > -1
            })

        address.name = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value

        const suggestionIndex = suggestions.findIndex((suggestion) => suggestion.name === event.target.value)

        const suggestion = suggestions[suggestionIndex]
        let timeslotFromAddressBook = false

        if (suggestion) {
            if (changeOnExactMatch) {
                address.attention = suggestion.attention || ''
                address.street = suggestion.street || ''
                address.nr = suggestion.nr || ''
                address.addition = suggestion.addition || ''
                address.street2 = suggestion.street2 || ''
                address.postalCode = suggestion.postalCode || ''
                address.city = suggestion.city || ''
                address.country = suggestion.country || 'NL'
                address.isBusiness = !!suggestion.isBusiness
                address.email = suggestion.email || address.email || ''
                address.phone = suggestion.phone || address.phone || ''
                address.comment = suggestion.comment || address.comment || ''
                address.instructions = suggestion.instructions || address.instructions || ''
                address.attachments = suggestion.attachments || []
                if (suggestion.position) {
                    address.position = suggestion.position
                } else {
                    delete address.position
                }

                // An empty time in the address book keeps the time in the form
                if (this.props.fillTimeslot) {
                    address.startTime = suggestion.startTime || address.startTime
                    address.endTime = suggestion.endTime || address.endTime
                    timeslotFromAddressBook = !!(suggestion.startTime || suggestion.endTime)
                }

                typeof this.props.onConfirm === 'function' && this.props.onConfirm()
            }
        }

        this.setState({suggestions: suggestions})

        if (this.props.onSave) {
            this.setState({address})
        } else {
            this.props.onChange(address, undefined, timeslotFromAddressBook)
        }
    }

    onChangeAddress(updatedAddress) {
        if (this.props.onSave) {
            const address = {...this.state.address, ...updatedAddress}

            if (!updatedAddress.position) {
                delete address.position
            }

            this.setState({address})
        } else {
            const address = {...this.props.address, ...updatedAddress}

            if (!updatedAddress.position) {
                delete address.position
            }

            const timeslotFromAddressBook = this.setTimeslotOfNamelessEntry(this.props.address, address)

            this.props.onChange(address, undefined, timeslotFromAddressBook)
        }
    }

    /**
     * An address book entry without a name applies to every order to that address, whatever the name of the recipient.
     * Fills its timeslot in when the address changes, the same way the server takes over its position.
     */
    setTimeslotOfNamelessEntry(previousAddress, address) {
        const {addressBook = [], fillTimeslot} = this.props
        const addressFields = ['street', 'nr', 'addition', 'postalCode', 'city', 'country']

        if (!fillTimeslot || addressFields.every((key) => previousAddress[key] === address[key])) {
            return false
        }

        const entry = addressBook.find((entry) => {
            return !entry.name &&
                (entry.startTime || entry.endTime) &&
                entry.postalCode === address.postalCode &&
                entry.nr === address.nr &&
                (entry.addition || '') === (address.addition || '') &&
                entry.country === address.country &&
                (!address.street || entry.street?.toLowerCase() === address.street.toLowerCase()) &&
                (!address.city || entry.city?.toLowerCase() === address.city.toLowerCase())
        })

        if (!entry) {
            return false
        }

        address.startTime = entry.startTime || address.startTime
        address.endTime = entry.endTime || address.endTime

        return true
    }

    onChangeValue(path, parser, event) {
        const {reseller, customer} = this.props

        if (!event) {
            event = parser
            parser = undefined
        }

        let address = {...this.props.address}

        if (this.props.onSave) {
            address = {...this.state.address}
        }

        if (typeof parser === 'function') {
            _.set(address, path, parser(event.target.value))
        } else {
            _.set(address, path, event.target.value)
        }

        if (path === 'type') {
            address.minimumAge = false
            Object.keys(dbCodes.pickupAndDeliveryOptions).forEach((option) => {
                address[option] = false
            })

            if (event.target.value === 'pickup') {
                let pickupOptions = reseller.settings.orders.defaultPickupOptions

                if (customer && customer.settings.orders.defaultPickupOptions && Array.isArray(customer.settings.orders.defaultPickupOptions)) {
                    pickupOptions = customer.settings.orders.defaultPickupOptions
                } else if (customer && customer.settings.orders.defaultPickupOptions === 'none') {
                    pickupOptions = []
                }

                const customOptions = []
                pickupOptions.forEach((option) => {
                    if (option in dbCodes.pickupAndDeliveryOptions) {
                        address[option] = true
                    } else {
                        customOptions.push(option)
                    }
                })

                address.customOptions = customOptions
            }

            if (event.target.value === 'delivery') {
                let deliveryOptions = reseller.settings.orders.defaultDeliveryOptions

                if (customer && customer.settings.orders.defaultDeliveryOptions && Array.isArray(customer.settings.orders.defaultDeliveryOptions)) {
                    deliveryOptions = customer.settings.orders.defaultDeliveryOptions
                } else if (customer && customer.settings.orders.defaultDeliveryOptions === 'none') {
                    deliveryOptions = []
                }

                const customOptions = []
                deliveryOptions.forEach((option) => {
                    if (option in dbCodes.pickupAndDeliveryOptions) {
                        if (option === 'minimumAge +16') {
                            address.minimumAge = 16
                        } else if (option === 'minimumAge +18') {
                            address.minimumAge = 18
                        } else if (option === 'minimumAge +21') {
                            address.minimumAge = 21
                        } else {
                            address[option] = true
                        }
                    } else {
                        customOptions.push(option)
                    }
                })

                address.customOptions = customOptions
            }
        }

        if (this.props.onSave) {
            this.setState({address})
        } else {
            this.props.onChange(address)
        }
    }

    onChangeOptions(event) {
        let address = {...this.props.address}

        if (this.props.onSave) {
            address = {...this.state.address}
        }

        address.gpsRequired = false
        address.signatureRequired = false
        address.signatureNeighbors = false
        address.nameRequired = false
        address.statedAddressOnly = false
        address.mailboxDelivery = false
        address.refrigerated = false
        address.scanRequired = false
        address.imageRequired = false
        address.minimumAge = false
        address.idCheck = false
        address.customOptions = []

        event.target.values.map((key) => {
            if (key === 'minimumAge +16') {
                address.minimumAge = 16
            } else if (key === 'minimumAge +18') {
                address.minimumAge = 18
            } else if (key === 'minimumAge +21') {
                address.minimumAge = 21
            } else if (key in dbCodes.pickupAndDeliveryOptions) {
                address[key] = true
            } else {
                address.customOptions = address.customOptions || []
                address.customOptions.push(key)
            }
        })

        if (this.props.onSave) {
            this.setState({address})
        } else {
            this.props.onChange(address, true)
        }
    }

    onChangeChecked(path) {
        let address = {...this.props.address}

        if (this.props.onSave) {
            address = {...this.state.address}
        }

        address.disabledDays = address.disabledDays || {}

        _.set(address, path, !_.get(address, path))

        if (this.props.onSave) {
            this.setState({address})
        } else {
            this.props.onChange(address)
        }
    }

    save() {
        if (this.props.onSave) {
            this.props.onSave(this.state.address)
        }

        if (this.state.address.save) {
            // The timeslot of the order is not the timeslot of the address in the address book
            AddressActions.update([_.omit(this.state.address, ['startTime', 'endTime'])])
        }
    }

    render() {
        let {id = '', editAddressTypeAllowed, address, showDays, availableDays, allowAttachments, readOnly, reseller} = this.props
        const {expanded, validation, suggestions} = this.state

        if (this.props.onSave) {
            address = {...this.state.address}
        }

        address.options = []

        if (address.gpsRequired) {
            address.options.push('gpsRequired')
        }

        if (address.signatureRequired) {
            address.options.push('signatureRequired')
        }

        if (address.signatureNeighbors) {
            address.options.push('signatureNeighbors')
        }

        if (address.nameRequired) {
            address.options.push('nameRequired')
        }

        if (address.statedAddressOnly) {
            address.options.push('statedAddressOnly')
        }

        if (address.mailboxDelivery) {
            address.options.push('mailboxDelivery')
        }

        if (address.refrigerated) {
            address.options.push('refrigerated')
        }

        if (address.scanRequired) {
            address.options.push('scanRequired')
        }

        if (address.imageRequired) {
            address.options.push('imageRequired')
        }

        if (address.minimumAge) {
            if (address.minimumAge === 16) {
                address.options.push('minimumAge +16')
            }

            if (address.minimumAge === 18) {
                address.options.push('minimumAge +18')
            }

            if (address.minimumAge === 21) {
                address.options.push('minimumAge +21')
            }
        }

        if (address.idCheck) {
            address.options.push('idCheck')
        }

        if (address.customOptions?.length > 0) {
            address.options.push(...address.customOptions)
        }

        const countries = [
            {value: 'NL', title: 'Nederland', bold: true},
            {value: 'BE', title: 'België', bold: true},
            {value: 'DE', title: 'Duitsland', bold: true},
            {value: 'FR', title: 'Frankrijk', bold: true},
            {value: 'IT', title: 'Italië', bold: true},
            {value: 'ES', title: 'Spanje', bold: true},
            {value: 'GB', title: 'Verenigd Koninkrijk', bold: true},
            {value: 'US', title: 'Verenigde Staten', bold: true}
        ]

        Countries.map((country) => {
            countries.push({value: country.code2, title: country.name})
        })

        return (
            <div style={{width: '100%', background: Colors.backgroundNeutral2}}>
                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <FormRow>
                        <FormRow style={{flex: 1}}>
                            <RadioGroup
                                disabled={!editAddressTypeAllowed || readOnly}
                                value={address.type}
                                options={[
                                    {value: 'pickup', label: 'Ophalen'},
                                    {value: 'delivery', label: 'Bezorgen'}
                                ]}
                                onChange={this.onChangeValue.bind(this, 'type')}
                            />

                            <RadioGroup
                                disabled={readOnly}
                                value={(!!address.isBusiness).toString()}
                                options={[
                                    {value: 'false', icon: 'mdi mdi-home', tooltip: Languages.privateAddress['NL']},
                                    {value: 'true', icon: 'mdi mdi-store', tooltip: Languages.businessAddress['NL']}
                                ]}
                                onChange={this.onChangeValue.bind(this, 'isBusiness', (value) => value === 'true')}
                            />
                        </FormRow>

                        <FormRow style={{flex: 1}}>
                            <TimePicker
                                disabled={readOnly}
                                label='Starttijd'
                                value={address.startTime}
                                onChange={this.onChangeValue.bind(this, 'startTime')}
                            />

                            <TimePicker
                                disabled={readOnly}
                                label='Eindtijd'
                                value={address.endTime}
                                onChange={this.onChangeValue.bind(this, 'endTime')}
                            />
                        </FormRow>
                    </FormRow>

                    <FormRow>
                        <Input
                            id={`name${id}`}
                            label='Naam'
                            value={address.name}
                            isInvalid={validation.name}
                            onChange={(event) => this.setName(event, false)}
                            suggestions={suggestions.map((suggestion) => {
                                const addressText = ` (${suggestion.street} ${suggestion.nr}${suggestion.addition}, ${suggestion.postalCode} ${suggestion.city})`
                                return {
                                    label: `${suggestion.name} ${addressText}`,
                                    value: suggestion.name
                                }
                            })}
                            onChooseSuggestion={this.setName.bind(this)}
                            suggestionFilter
                            autoComplete='off'
                            disabled={readOnly}
                        />

                        {address.type === 'pickup' &&
                            <Select
                                label='Opties'
                                values={address.options}
                                options={[
                                    ...Object.entries(dbCodes.pickupOptions).map(([value, title]) => ({value, title})).toSorted((a, b) => a.title.localeCompare(b.title)),
                                    ...(reseller?.settings.orders.customOptions || []).map((option) => ({
                                        value: option.id,
                                        title: option.name,
                                        icon: 'mdi mdi-circle',
                                        iconColor: option.color,
                                        showIconInValue: true
                                    }))
                                ]}
                                onChange={this.onChangeOptions.bind(this)}
                                disabled={readOnly}
                            />
                        }

                        {address.type === 'delivery' &&
                            <Select
                                label='Opties'
                                values={address.options}
                                options={[
                                    ...Object.entries(dbCodes.pickupAndDeliveryOptions).map(([value, title]) => ({value, title})).toSorted((a, b) => a.title.localeCompare(b.title)),
                                    ...(reseller?.settings.orders.customOptions || []).map((option) => ({
                                        value: option.id,
                                        title: option.name,
                                        icon: 'mdi mdi-circle',
                                        iconColor: option.color,
                                        showIconInValue: true
                                    }))
                                ]}
                                onChange={this.onChangeOptions.bind(this)}
                                disabled={readOnly}
                            />
                        }
                    </FormRow>

                    <FormRow>
                        {!expanded &&
                            <AddressSearch
                                id={`address${id}`}
                                address={address}
                                onChange={this.onChangeAddress.bind(this)}
                                disabled={readOnly}
                            />
                        }

                        {expanded &&
                            <Input
                                id={`attention${id}`}
                                label='Ter attentie van'
                                value={address.attention}
                                onChange={this.onChangeValue.bind(this, 'attention')}
                                disabled={readOnly}
                            />
                        }

                        <TextArea
                            label='Instructies'
                            value={address.instructions}
                            onChange={this.onChangeValue.bind(this, 'instructions')}
                            disabled={readOnly}
                        />
                    </FormRow>

                    {expanded &&
                        <>
                            <div style={{marginTop: 12}}>
                                <PostalCodeAutocomplete id={`postalCode${id}`} disabled={readOnly} address={address} onChange={this.onChangeAddress.bind(this)} changeFocusAfterPostalCode={false} />
                            </div>

                            <FormRow style={{flexWrap: 'wrap'}}>
                                <Input
                                    id={`street${id}`}
                                    style={{minWidth: 200}}
                                    label='Straat'
                                    value={address.street || ''}
                                    isInvalid={validation.street}
                                    onChange={this.onChangeValue.bind(this, 'street')}
                                    disabled={readOnly}
                                />

                                <Input
                                    style={{minWidth: 200}}
                                    label='2e adres regel'
                                    value={address.street2}
                                    onChange={this.onChangeValue.bind(this, 'street2')}
                                    disabled={readOnly}
                                />
                            </FormRow>

                            <FormRow style={{flexWrap: 'wrap'}}>
                                <Input
                                    id={`city${id}`}
                                    style={{minWidth: 200}}
                                    label='Plaats'
                                    value={address.city}
                                    isInvalid={validation.city}
                                    onChange={this.onChangeValue.bind(this, 'city')}
                                    disabled={readOnly}
                                />

                                <Select
                                    style={{minWidth: 200}}
                                    label='Land'
                                    value={address.country}
                                    options={countries}
                                    onChange={this.onChangeValue.bind(this, 'country')}
                                    disabled={readOnly}
                                />
                            </FormRow>

                            <FormRow style={{flexWrap: 'wrap'}}>
                                <Input
                                    style={{minWidth: 200}}
                                    label='Emailadres'
                                    value={address.email}
                                    onChange={this.onChangeValue.bind(this, 'email')}
                                    disabled={readOnly}
                                />

                                <Input
                                    style={{minWidth: 200}}
                                    label='Telefoon'
                                    value={address.phone}
                                    onChange={this.onChangeValue.bind(this, 'phone')}
                                    disabled={readOnly}
                                />
                            </FormRow>

                            <FormRow>
                                <FormStack style={{flex: 1}}>
                                    <Check
                                        style={{minWidth: 200}}
                                        label='Opslaan in adresboek'
                                        checked={address.save}
                                        onChange={this.onChangeChecked.bind(this, 'save')}
                                        disabled={readOnly}
                                    />

                                    {allowAttachments && showDays &&
                                        <AddressAttachment
                                            edit={!readOnly}
                                            address={address}
                                            onChange={this.onChangeAddress.bind(this)}
                                            fileUploadStyle={{height: 36}}
                                        />
                                    }
                                </FormStack>

                                {allowAttachments && !showDays &&
                                    <div style={{flex: 1, marginLeft: 12}}>
                                        <AddressAttachment
                                            edit={!readOnly}
                                            address={address}
                                            onChange={this.onChangeAddress.bind(this)}
                                            fileUploadStyle={{height: 36}}
                                        />
                                    </div>
                                }

                                {showDays &&
                                    <div style={{display: 'flex', flex: 1, flexWrap: 'wrap', marginLeft: 6}}>
                                        {[
                                            {title: 'M', value: 'monday'},
                                            {title: 'D', value: 'tuesday'},
                                            {title: 'W', value: 'wednesday'},
                                            {title: 'D', value: 'thursday'},
                                            {title: 'V', value: 'friday'},
                                            {title: 'Z', value: 'saturday'},
                                            {title: 'Z', value: 'sunday'}
                                        ].map((entry) => {
                                            return (
                                                <Option
                                                    key={entry.value}
                                                    label={entry.title}
                                                    checked={!address.disabledDays?.[entry.value]}
                                                    disabled={!availableDays[entry.value] || readOnly}
                                                    onChange={this.onChangeChecked.bind(this, `disabledDays.${entry.value}`)}
                                                />
                                            )
                                        })}
                                    </div>
                                }
                            </FormRow>
                        </>
                    }
                </FormStack>

                <Row
                    style={{justifyContent: 'center', cursor: 'pointer'}}
                    onClick={() => {
                        this.setState({expanded: !expanded})
                    }}
                >
                    {expanded ?
                        <i className='mdi mdi-chevron-up' /> :
                        <i className='mdi mdi-chevron-down' />
                    }
                </Row>
            </div>
        )
    }
}

export default EditAddress
